import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle2, Clock, ShieldCheck, Lock, ChevronDown, ChevronUp, Heart, ChevronRight, Footprints, GraduationCap } from 'lucide-react';
const CHECKOUT_URL = 'https://afrekenen.stresslessdogs.nl/checkout/stressless-wandelen-m-korting';
function useCountdown() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const day = now.getDay();
      const diffToSunday = day === 0 ? 0 : 7 - day;
      const nextSunday = new Date(now);
      nextSunday.setDate(now.getDate() + diffToSunday);
      nextSunday.setHours(23, 59, 59, 999);
      const difference = nextSunday.getTime() - now.getTime();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(difference / (1000 * 60 * 60) % 24),
          minutes: Math.floor(difference / 1000 / 60 % 60),
          seconds: Math.floor(difference / 1000 % 60)
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);
  return timeLeft;
}
export function StresslessWandelenPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const courseImages = [{
    src: 'https://cdn.magicpatterns.com/uploads/cS8Dp82nw9bgvjp4Tm4gfx/MC_SW_foto_2.png',
    alt: 'Ontspannen wandelen met je hond aan een losse lijn'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/43G5tgeSGqJzu7vhxyBGxT/MC_SW_foto_8.png',
    alt: 'Les 5 uit de cursus: Extra tips voor stevige trekkers'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/ehQTbcqq7j8ic9NyiyVvC8/MC_SW_foto_7.png',
    alt: 'Les: Leren lopen met een slappe lijn - de eerste stap'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/r3nRpfgd61JBaqBTB8tM2p/MC_SW_foto_6.png',
    alt: 'Uitleg over het stresslevel van je hond herkennen'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/pPQK41mnDGXP3s8zBbmmMv/MC_SW_foto_1.png',
    alt: 'Les 1 uit de cursus: Het begint met een goed tuig'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/4xnzLYTZjUA8X5m6itpPv2/MC_SW_foto_3.png',
    alt: 'Werken met de lijn in een drukke omgeving'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/nyu5SZPibXi6MZoLCQnD8j/MC_SW_foto_4.png',
    alt: 'Rustig en ontspannen snuffelen tijdens de wandeling'
  }];
  const [selectedImage, setSelectedImage] = useState(0);
  const trustpilotGridRef = useRef<HTMLDivElement>(null);
  const {
    days,
    hours,
    minutes,
    seconds
  } = useCountdown();
  useEffect(() => {
    if ((window as any).Trustpilot && trustpilotGridRef.current) {
      ;
      (window as any).Trustpilot.loadFromElement(trustpilotGridRef.current, true);
    }
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Is deze cursus geschikt voor een puppy?',
    a: 'Ja, absoluut! Hoe eerder je begint met de juiste communicatie, hoe beter. Je voorkomt hiermee dat je puppy verkeerd gedrag aanleert.'
  }, {
    q: 'Kun je de cursus volgen met een senior hond?',
    a: 'Zeker. Honden zijn nooit te oud om te leren. Omdat we werken vanuit ontspanning en communicatie (en niet met fysieke correcties), is dit juist heel fijn voor oudere honden.'
  }, {
    q: 'Mijn hond is onzeker of bang, is dit geschikt?',
    a: 'Juist voor onzekere honden is deze methode ideaal. We bouwen aan zelfvertrouwen en een veilige band, waardoor de angst op straat drastisch zal verminderen.'
  }, {
    q: 'Mijn hond vertoont probleemgedrag, kan ik meedoen?',
    a: 'Ja. Trekken, uitvallen, blaffen of bevriezen zijn allemaal symptomen van stress of onbegrip. Door de oorzaak aan te pakken met onze methode, verdwijnt het probleemgedrag vaak vanzelf.'
  }, {
    q: 'Heb ik bepaalde materialen nodig?',
    a: 'Een goed passend Y-tuigje en een gewone, vaste lijn van minimaal 2 meter is alles wat je nodig hebt. Geen slipkettingen of anti-trektuigen!'
  }, {
    q: 'Hoe lang heb ik toegang?',
    a: 'Je krijgt levenslange toegang tot al het lesmateriaal, inclusief toekomstige updates.'
  }];
  const formatTime = (num: number) => num.toString().padStart(2, '0');
  return <div className="min-h-screen bg-[#FAFAF7] font-sans text-gray-800 selection:bg-[#e85d75] selection:text-white pb-20 md:pb-0" data-id="element-3110" data-mp="15pdywds8sytv">
      {/* 1. STICKY HEADER BAR */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-[#1a2b4a] text-white shadow-md" data-id="element-3111" data-mp="1fmshaixq7xot">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2" data-id="element-3112" data-mp="1fkxvl2ems1jo">
          <div className="flex items-center gap-2 text-sm font-medium" data-id="element-3113" data-mp="3hex4jh4a66t7">
            <Clock className="w-4 h-4 text-[#e85d75] flex-shrink-0" data-id="element-3114" data-mp="37a6wqum748h2" />
            <span data-id="element-3115" data-mp="37b0705kd5g3y">
              ⏰ Actie eindigt over:{' '}
              <span className="font-mono font-bold text-[#e85d75]" data-id="element-3116" data-mp="3bc1z82fv4rwf">
                {formatTime(days)}:{formatTime(hours)}:{formatTime(minutes)}:
                {formatTime(seconds)}
              </span>
            </span>
          </div>
          <a href={CHECKOUT_URL} className="text-white/80 hover:text-white text-xs sm:text-sm font-medium transition-colors sm:bg-[#e85d75] sm:hover:bg-[#d64c64] sm:text-white sm:px-4 sm:py-1.5 sm:rounded-full sm:font-bold whitespace-nowrap" data-id="element-3117" data-mp="32hnng9ujjzi2">
            Bekijk de actie ↓
          </a>
        </div>
      </div>

      {/* 2. HERO SECTION */}
      <section className="pt-28 md:pt-32 pb-12 md:pb-20 px-4 bg-white" data-id="element-3118" data-mp="2jlfdwtuhx2gy">
        <div className="max-w-5xl mx-auto flex flex-col md:grid md:grid-cols-2 md:gap-12 md:items-center" data-id="element-3119" data-mp="ctmuh9wifh1c">
          <div className="w-full rounded-2xl overflow-hidden shadow-lg order-2 mt-8 mb-0 md:mt-0 md:mb-0 md:order-2 aspect-[10/7] md:aspect-square" data-id="element-3120" data-mp="htj3jnk2dynr">
            <img src="https://cdn.magicpatterns.com/uploads/pC14pGq8sFZiGRj4x9WmKf/Amee_wandelen_foto.png" alt="Amee wandelt ontspannen met haar hond" className="w-full h-full object-cover object-[50%_55%] md:object-center" data-id="element-3121" data-mp="fci0hyed0typ" />
          </div>

          <div className="flex flex-col items-center text-center order-1 md:items-start md:text-left md:order-1" data-id="element-3122" data-mp="1gxs3ww9bu5j">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#1a2b4a] leading-tight mb-4" data-id="element-3123" data-mp="u3ih4qp9fu5x">
              Maak van jullie wandeling (weer) het leukste moment van de dag!
            </h1>

            <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-3124" data-mp="kdp91zobtx6f">
              De complete online methode om wandelproblemen stap voor stap op te
              lossen - zonder dwang, zonder correcties.
            </p>

            <div className="inline-flex items-center gap-2 bg-red-50 text-[#e85d75] text-sm font-bold px-4 py-2 rounded-full mb-6 border border-red-100" data-id="element-3125" data-mp="1d1bhbl8i1pzm">
              🔥 Alleen dit weekend: 50% korting
            </div>

            <div className="flex items-center justify-center md:justify-start gap-3 mb-2" data-id="element-3126" data-mp="1pcug8te7kmqf">
              <span className="text-2xl text-gray-400 line-through font-medium" data-id="element-3127" data-mp="1ua42y1a7qkak">
                €87
              </span>
              <span className="text-5xl font-extrabold text-[#e85d75]" data-id="element-3128" data-mp="1z8p360pgt3z0">
                €43,50
              </span>
            </div>
            <p className="text-sm font-medium text-gray-500 mb-8" data-id="element-3129" data-mp="28ayc4m534paa">
              t/m zondag 28 juni 23:59
            </p>

            <a href={CHECKOUT_URL} className="flex items-center justify-center w-full md:w-auto bg-[#e85d75] hover:bg-[#d64c64] text-white font-bold py-4 px-6 md:px-10 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl mb-4" data-id="element-3130" data-mp="2d3803wmn3f9x">
              Ja, ik wil de cursus met 50% korting →
            </a>

            <div className="flex justify-center md:justify-start items-center gap-2 text-sm text-gray-500 font-medium" data-id="element-3131" data-mp="2qzlf8z3pb4l8">
              <ShieldCheck className="w-4 h-4 text-green-600" data-id="element-3132" data-mp="38bkwnknrage9" />
              14 dagen niet-goed-geld-terug garantie
            </div>
          </div>
        </div>
      </section>

      {/* 3. PROBLEM SECTION */}
      <section className="py-12 md:py-20 px-4 bg-[#FAFAF7]" data-id="element-3133" data-mp="3hbhileirh15m">
        <div className="max-w-3xl mx-auto" data-id="element-3134" data-mp="3h9wotr49tm8t">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1a2b4a] mb-6 text-center md:text-left" data-id="element-3135" data-mp="2tln6iz69ngmf">
            Zie je op tegen de dagelijkse wandelingen?
          </h2>

          <p className="text-base text-gray-700 mb-6" data-id="element-3136" data-mp="33by8ig6e3fqo">
            Merk je bijvoorbeeld dat je hond:
          </p>

          <ul className="space-y-5 mb-8" data-id="element-3137" data-mp="2ykzpv9xxi5k9">
            {['het liefst snel naar huis wil (of zelfs niet het huis uit wil zodra je de lijn pakt)', 'schrikt van geluiden, vreemde voorwerpen of onverwachte situaties', 'hard trekt aan de lijn', "blaft naar van alles (honden, katten, fietsers, scooters, auto's etc.)", 'opspringt tegen mensen'].map((point, i) => <li key={i} className="flex items-start" data-id="element-3138" data-mp="q6t9y52xpgid" data-mpi={i}>
                <ChevronRight className="w-5 h-5 text-[#e85d75] mr-3 flex-shrink-0 mt-0.5" data-id="element-3139" data-mp="cewjs6kttgom" />
                <span className="text-gray-700 text-base leading-relaxed" data-id="element-3140" data-mp="gf712fe2jtis">
                  {point}
                </span>
              </li>)}
          </ul>

          <p className="text-base text-gray-700 leading-relaxed" data-id="element-3141" data-mp="143gdwrtb16xs">
            Je hebt waarschijnlijk al van alles geprobeerd. En je bent niet de
            enige. Veel baasjes grijpen uit wanhoop naar 'oplossingen' die het
            probleem eigenlijk alleen maar erger maken.
          </p>
        </div>
      </section>

      {/* 4. SOLUTION/PHILOSOPHY SECTION */}
      <section className="py-12 md:py-20 px-4 bg-white" data-id="element-3142" data-mp="3ft93p23phruw">
        <div className="max-w-5xl mx-auto md:grid md:grid-cols-2 md:gap-12 md:items-center" data-id="element-3143" data-mp="3kugaoyy0yn0i">
          <div className="w-full rounded-2xl overflow-hidden shadow-lg mb-8 md:mb-0 aspect-square" data-id="element-3144" data-mp="1alcvjh1p4z9i">
            <img src="https://cdn.magicpatterns.com/uploads/iiXgePNM5Tv3dNugSrv7jx/Ontwerp_zonder_titel_(74).png" alt="Baasje zit ontspannen in het gras met haar hond" className="w-full h-full object-cover" data-id="element-3145" data-mp="1enzppoluaw9v" />
          </div>

          <div data-id="element-3146" data-mp="qxvplr7v1enm">
            <div className="space-y-5 text-base md:text-lg text-gray-700 leading-relaxed mb-8" data-id="element-3147" data-mp="qxdw32cl1eje">
              <p data-id="element-3148" data-mp="vxi19w81imub">
                Wandelen is teamwork. Het is geen strijd om wie de baas is, maar
                een samenwerking gebaseerd op liefdevolle communicatie en de
                juiste begeleiding bieden aan je hond.
              </p>
              <p data-id="element-3149" data-mp="mwk7e6rbfvpg">
                Wanneer je hem begrijpt en op de juiste manier begeleid zal je
                zien dat alles prettiger gaat. Zijn gedrag is namelijk geen
                ongehoorzaamheid, het is een uiting van spanning, angst of
                overprikkeling.
              </p>
              <p data-id="element-3150" data-mp="2reruxu3kdmm2">
                In de Stressless Wandelen cursus leer je hoe herkent wat er
                speelt én leer je methodes waarmee je jouw hond kunt helpen
                ontspannen. Zonder dwang, zonder pijn, maar met wederzijds
                respect.
              </p>
            </div>

            <a href={CHECKOUT_URL} className="flex items-center justify-center w-full md:w-auto md:inline-flex bg-[#e85d75] hover:bg-[#d64c64] text-white font-bold py-4 px-6 md:px-10 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl" data-id="element-3151" data-mp="2vek52b8lw0tj">
              Ja, ik wil de cursus met 50% korting →
            </a>
          </div>
        </div>
      </section>

      {/* 5. WAAROM DIT DE SLIMSTE KEUZE IS BIJ WANDELPROBLEMEN */}
      <section className="py-12 md:py-20 px-4 bg-[#FAFAF7]" data-id="element-3152" data-mp="1osg4eenl27c3">
        <div className="max-w-5xl mx-auto" data-id="element-3153" data-mp="1kptc6cn02ked">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1a2b4a] mb-8 md:mb-12 text-center" data-id="element-3154" data-mp="1kr4o4zqlmygk">
            Waarom dit de slimste keuze is bij wandelproblemen
          </h2>

          <div className="space-y-6 md:space-y-0 md:grid md:grid-cols-3 md:gap-6 mb-8 md:mb-12" data-id="element-3155" data-mp="3osuv0sewapt2">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-start" data-id="element-3156" data-mp="3tshjjy06bsh6">
              <div className="bg-red-50 p-3 rounded-full mb-4" data-id="element-3157" data-mp="2nhdpdajmr5c">
                <Heart className="w-6 h-6 text-[#e85d75]" data-id="element-3158" data-mp="6ob36b3mwe0c" />
              </div>
              <h3 className="font-bold text-[#1a2b4a] mb-2 text-lg" data-id="element-3159" data-mp="6m71lzt7jr38">
                Geen correcties of straf
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-3160" data-mp="gdb625l9btjt">
                De methode is gebaseerd op liefdevolle communicatie, niet op
                dwang of pijnlijke hulpmiddelen.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-start" data-id="element-3161" data-mp="qd1zs51zu4iu">
              <div className="bg-green-50 p-3 rounded-full mb-4" data-id="element-3162" data-mp="ue5f8qg4pyjj">
                <Footprints className="w-6 h-6 text-[#2E7D5B]" data-id="element-3163" data-mp="2evwrryhfcmmi" />
              </div>
              <h3 className="font-bold text-[#1a2b4a] mb-2 text-lg" data-id="element-3164" data-mp="2aub55lf38tz4">
                Stap voor stap in het tempo van jouw hond
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-3165" data-mp="25vi3qrd2stfb">
                Opgebouwd zodat je hond rustig kan leren, zonder overprikkeling.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-start" data-id="element-3166" data-mp="2yim1qcdr47hl">
              <div className="bg-blue-50 p-3 rounded-full mb-4" data-id="element-3167" data-mp="2tlvm1q5uzdwp">
                <GraduationCap className="w-6 h-6 text-blue-600" data-id="element-3168" data-mp="2otaukvafdcwc" />
              </div>
              <h3 className="font-bold text-[#1a2b4a] mb-2 text-lg" data-id="element-3169" data-mp="2jv0zslsm4eb9">
                Gebaseerd op de bewezen communicatie-methode
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-3170" data-mp="3hbpyi6m9n0gf">
                Dezelfde aanpak uit het webinar, specifiek toegepast op de
                wandeling.
              </p>
            </div>
          </div>

          <ul className="space-y-4 md:max-w-2xl md:mx-auto" data-id="element-3171" data-mp="2x03yva0hzkxh">
            <li className="flex items-start" data-id="element-3172" data-mp="3138x30djp0gb">
              <CheckCircle2 className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-3173" data-mp="310w8i4da7rua" />
              <span className="text-gray-700 font-medium leading-relaxed" data-id="element-3174" data-mp="2n527e7ir26rq">
                Levenslang toegang waardoor je vandaag kan starten, maar zelf
                het tempo bepaald
              </span>
            </li>
            <li className="flex items-start" data-id="element-3175" data-mp="3kqzwinrmxacn">
              <CheckCircle2 className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-3176" data-mp="3kpohfrsqog68" />
              <span className="text-gray-700 font-medium leading-relaxed" data-id="element-3177" data-mp="36124fwql40i6">
                14 dagen niet-goed, geld terug garantie
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* 6. CURRICULUM / WAT ZIT ER IN DE CURSUS? */}
      <section className="py-12 md:py-20 px-4 bg-[#1a2b4a] text-white" data-id="element-3178" data-mp="7c3ciswz20pq">
        <div className="max-w-4xl mx-auto" data-id="element-3179" data-mp="7e68ruopwq2w">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 md:mb-12 text-center" data-id="element-3180" data-mp="1s1z05j0c5yey">
            Wat zit er in de cursus?
          </h2>

          <ul className="space-y-4 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-4 md:space-y-0 mb-10" data-id="element-3181" data-mp="1n2w1cgay8off">
            <li className="flex items-start" data-id="element-3182" data-mp="20u0v51rmymhu">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-3183" data-mp="1wtgvnrjlb6sj" />
              <div className="flex-1" data-id="element-3184" data-mp="193cvju5m1p6a">
                <span className="font-medium" data-id="element-3185" data-mp="194flnzv3znl5">
                  Module 1: Hoe verkeerd riem/lijn/halsband/harnas problemen
                  vergroot
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-3186" data-mp="1i4uavab71mtx">- €27</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-3187" data-mp="1d46juomyt5zp">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-3188" data-mp="1d7c6lx84fvs9" />
              <div className="flex-1" data-id="element-3189" data-mp="2tlkjt1704sr8">
                <span className="font-medium" data-id="element-3190" data-mp="2okwhvkh8iri8">
                  Module 2: Hoe een hond het wandelen ziet
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-3191" data-mp="1m671uyjr3ff7">- €27</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-3192" data-mp="1r4icvcls7vz0">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-3193" data-mp="1v7wug7pdckek" />
              <div className="flex-1" data-id="element-3194" data-mp="206rjhrdxj574">
                <span className="font-medium" data-id="element-3195" data-mp="205otdlofl6s9">
                  Module 3: Het stresslevel van je hond herkennen (en verlagen)
                  voor ontspannen start
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-3196" data-mp="17haul3piq6fn">- €37</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-3197" data-mp="1cgdt05azteen">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-3198" data-mp="1h7vs0ntgh4wz" />
              <div className="flex-1" data-id="element-3199" data-mp="1h9qgud7ulh6c">
                <span className="font-medium" data-id="element-3200" data-mp="jt9sacixnm8o">
                  Module 4: Hoe gedragsexperts omgaan met de lijn
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-3201" data-mp="oprw9a6fvgjy">- €47</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-3202" data-mp="h5zcclam3vqw">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-3203" data-mp="c8pi8sca2vwq" />
              <div className="flex-1" data-id="element-3204" data-mp="79csnzu0vaqg">
                <span className="font-medium" data-id="element-3205" data-mp="78ba7aaoflyp">
                  Module 5: Zorgen voor maximaal ontspannen (en rustige) hond
                  tijdens wandelen
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-3206" data-mp="3ufdlsdrojb0a">- €47</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-3207" data-mp="3pmarg3f2vxj1">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-3208" data-mp="3koiwt1n1txb5" />
              <div className="flex-1" data-id="element-3209" data-mp="3fpplnfv8iie5">
                <span className="font-medium" data-id="element-3210" data-mp="3fqqxvnnzlaxg">
                  Module 6: Omgaan met probleemsituaties (technieken van
                  professionals)
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-3211" data-mp="36ohmywot3fjj">- €47</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-3212" data-mp="yhcrpv5raji6">
              <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-3213" data-mp="ye8iukhcizl6" />
              <div className="flex-1" data-id="element-3214" data-mp="13dbmc0hogxr1">
                <span className="font-medium" data-id="element-3215" data-mp="pg683d7t8skp">
                  Module 7: Trekken aan de lijn aanpakken in drie stappen
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-3216" data-mp="tfnf4b2vxxrz">- €37</span>
              </div>
            </li>

            <li className="flex items-start pt-4" data-id="element-3217" data-mp="fnqoycks1xy8">
              <span className="text-xl mr-3 flex-shrink-0 mt-0.5" data-id="element-3218" data-mp="kgtkurd11jhl">🎁</span>
              <div className="flex-1" data-id="element-3219" data-mp="5rxgb4a6iolq">
                <span className="font-medium text-blue-200" data-id="element-3220" data-mp="5syrr9v3xd3z">
                  BONUS 1: "Succesvol Wandelen Start Binnen" Online Checklist
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-3221" data-mp="3swlq7g1zy36s">- €19</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-3222" data-mp="1qbw0xvbf81i">
              <span className="text-xl mr-3 flex-shrink-0 mt-0.5" data-id="element-3223" data-mp="1p99f250zrre">🎁</span>
              <div className="flex-1" data-id="element-3224" data-mp="3eafzo5ff342a">
                <span className="font-medium text-blue-200" data-id="element-3225" data-mp="3ebhl93u278y9">
                  BONUS 2: Het Online Stappenplan "In de auto is leuk"
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-3226" data-mp="3nb4daz0wf2en">- €15</span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-3227" data-mp="3j76fcrnb7xn6">
              <span className="text-xl mr-3 flex-shrink-0 mt-0.5" data-id="element-3228" data-mp="12v2c7rqf18a">🎁</span>
              <div className="flex-1" data-id="element-3229" data-mp="10s0mqh7vjnq">
                <span className="font-medium text-blue-200" data-id="element-3230" data-mp="3sa7j4lopdcno">
                  BONUS 3: De Online "5 Kalmerende Activiteiten Voor Tijdens Het
                  Wandelen"
                </span>
                <span className="block text-gray-400 text-sm mt-1" data-id="element-3231" data-mp="5tbfrrvjymun">- €27</span>
              </div>
            </li>
          </ul>

          {/* COURSE PHOTO CATALOG */}
          <div className="mb-10 md:max-w-2xl md:mx-auto" data-id="element-3232" data-mp="jr9p2iv0nt3a">
            <h3 className="text-xl md:text-2xl font-bold text-center text-white mb-2" data-id="element-3233" data-mp="evudjxkhe9a1">
              Kijk alvast in de cursus
            </h3>
            <p className="text-center text-gray-300 text-sm md:text-base mb-6" data-id="element-3234" data-mp="rf3wtmmmma7q">
              Een blik op de lessen en wat je leert tijdens het wandelen.
            </p>
            <div className="overflow-hidden rounded-2xl bg-black/20 ring-1 ring-white/10" data-id="element-3235" data-mp="11axbpcntifso">
              <img src={courseImages[selectedImage].src} alt={courseImages[selectedImage].alt} className="w-full aspect-video object-cover" data-id="element-3236" data-mp="wcvrgoenoafm" />
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2 md:gap-3" data-id="element-3237" data-mp="3lluqi3walek">
              {courseImages.map((image, index) => <button key={image.src} type="button" onClick={() => setSelectedImage(index)} aria-label={`Bekijk foto ${index + 1}`} aria-pressed={selectedImage === index} className={`overflow-hidden rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-[#e85d75] ${selectedImage === index ? 'ring-2 ring-[#e85d75]' : 'ring-1 ring-white/10 opacity-70 hover:opacity-100'}`} data-id="element-3238" data-mp="dluv884x6szr" data-mpi={index}>
                  <img src={image.src} alt={image.alt} className="w-full aspect-video object-cover" data-id="element-3239" data-mp="1nke2dialhrgu" />
                </button>)}
            </div>
          </div>

          <div className="bg-white/10 rounded-2xl p-6 md:p-8 text-center mb-8 md:max-w-lg md:mx-auto" data-id="element-3240" data-mp="2fbtqxhvppo9p">
            <p className="text-lg text-gray-300 mb-2" data-id="element-3241" data-mp="2k80ux54jspp0">Totale waarde: €330</p>
            <p className="text-xl text-gray-400 line-through mb-2" data-id="element-3242" data-mp="269u60ehku01h">
              Normale prijs: €87
            </p>
            <p className="text-4xl md:text-5xl font-extrabold text-[#e85d75]" data-id="element-3243" data-mp="2b7lzve1s7w8b">
              Vandaag: €43,50
            </p>
          </div>

          <a href={CHECKOUT_URL} className="flex items-center justify-center w-full md:w-auto md:max-w-md md:mx-auto bg-[#e85d75] hover:bg-[#d64c64] text-white font-bold py-4 px-6 md:px-10 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl" data-id="element-3244" data-mp="zut6xzx991qo">
            Ja, ik wil de cursus met 50% korting →
          </a>
        </div>
      </section>

      {/* 7. HOE HET WERKT */}
      <section className="py-12 md:py-20 px-4 bg-white" data-id="element-3245" data-mp="2nsubgduaklsq">
        <div className="max-w-5xl mx-auto" data-id="element-3246" data-mp="2nq83id3ho49z">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#1a2b4a] mb-10 md:mb-16" data-id="element-3247" data-mp="2it7upuf98nf6">
            Hoe het werkt
          </h2>

          <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-3 md:gap-10 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px before:h-full before:w-0.5 before:bg-gray-200 md:before:hidden" data-id="element-3248" data-mp="2dvy080d0dlkh">
            <div className="relative flex items-start md:flex-col md:items-center md:text-center" data-id="element-3249" data-mp="73f096twrebq">
              <div className="w-12 h-12 bg-[#e85d75] text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0 z-10 shadow-md md:mb-5" data-id="element-3250" data-mp="31trnxoepm7c">
                1
              </div>
              <div className="ml-6 pt-2 md:ml-0 md:pt-0" data-id="element-3251" data-mp="1py9sde7jd6yg">
                <p className="text-gray-700 font-medium leading-relaxed" data-id="element-3252" data-mp="1pwoyzrwyju26">
                  Klik op een rode knop en reken af in onze veilige online
                  winkel
                </p>
              </div>
            </div>

            <div className="relative flex items-start md:flex-col md:items-center md:text-center" data-id="element-3253" data-mp="120km8ew1h9mk">
              <div className="w-12 h-12 bg-[#e85d75] text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0 z-10 shadow-md md:mb-5" data-id="element-3254" data-mp="16znsg2nr9lw4">
                2
              </div>
              <div className="ml-6 pt-2 md:ml-0 md:pt-0" data-id="element-3255" data-mp="1g03st397ccwr">
                <p className="text-gray-700 font-medium leading-relaxed" data-id="element-3256" data-mp="1g1n9uw2omrzl">
                  Via de mail ontvang je in enkele minuten inlog tot uitleg over
                  de methode.
                </p>
              </div>
            </div>

            <div className="relative flex items-start md:flex-col md:items-center md:text-center" data-id="element-3257" data-mp="2py3hmvlbekza">
              <div className="w-12 h-12 bg-[#e85d75] text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0 z-10 shadow-md md:mb-5" data-id="element-3258" data-mp="2kziiz0lpo9cy">
                3
              </div>
              <div className="ml-6 pt-2 md:ml-0 md:pt-0" data-id="element-3259" data-mp="2uwwymxzt8u47">
                <p className="text-gray-700 font-medium leading-relaxed" data-id="element-3260" data-mp="2uzj5evevn5ld">
                  Pas de methodes toe en zie hoe jullie wandelingen veranderen
                  naar het leukste moment van de dag!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. PRICING CTA SECTION */}
      <section className="py-12 md:py-20 px-4 bg-[#FAFAF7]" data-id="element-3261" data-mp="3dnee40vmtt7m">
        <div className="max-w-lg mx-auto bg-white rounded-3xl p-6 md:p-10 shadow-xl border border-gray-100 text-center" data-id="element-3262" data-mp="3dq0sajd136yt">
          <div className="inline-block bg-green-100 text-green-800 text-sm font-bold px-4 py-1.5 rounded-full mb-6" data-id="element-3263" data-mp="3mow0g0zmur4r">
            Je bespaart €43,50
          </div>

          <div className="flex items-center justify-center gap-3 mb-2" data-id="element-3264" data-mp="a5te3fn54du5">
            <span className="text-2xl text-gray-400 line-through font-medium" data-id="element-3265" data-mp="f2s4bn3oo0n2">
              €87
            </span>
            <span className="text-5xl font-extrabold text-[#1a2b4a]" data-id="element-3266" data-mp="13sien7k1zul">
              €43,50
            </span>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm font-bold text-[#e85d75] mb-8 bg-red-50 py-2 px-4 rounded-lg mt-6" data-id="element-3267" data-mp="57rtgqa2g6gk">
            <Clock className="w-4 h-4" data-id="element-3268" data-mp="3sawnhck53c0d" />
            Actie eindigt over: {formatTime(days)}:{formatTime(hours)}:
            {formatTime(minutes)}:{formatTime(seconds)}
          </div>

          <a href={CHECKOUT_URL} className="flex items-center justify-center w-full bg-[#e85d75] hover:bg-[#d64c64] text-white font-bold py-4 px-6 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl mb-6" data-id="element-3269" data-mp="3ikabrtfjn5my">
            Ja, ik wil de cursus met 50% korting →
          </a>

          <div className="flex flex-col items-center gap-3 text-sm text-gray-500 font-medium" data-id="element-3270" data-mp="1f4ykw89411dm">
            <div className="flex items-center gap-4" data-id="element-3271" data-mp="1acyhu6xqtsct">
              <span className="flex items-center" data-id="element-3272" data-mp="1abdi82wknj83">
                <ShieldCheck className="w-4 h-4 mr-1 text-green-600" data-id="element-3273" data-mp="15f58x6522k8u" /> Veilige
                betaling
              </span>
              <span className="flex items-center" data-id="element-3274" data-mp="11ed0ug7x6le5">
                <Lock className="w-4 h-4 mr-1 text-gray-400" data-id="element-3275" data-mp="1y3atnsw4098o" /> Direct toegang
              </span>
            </div>
            <span className="flex items-center mt-2" data-id="element-3276" data-mp="1t4r8hupcb1hd">
              14 dagen geld-terug garantie
            </span>
          </div>
        </div>
      </section>

      {/* 8b. SOCIAL PROOF - TRUSTPILOT */}
      <section className="py-12 md:py-20 px-4 bg-white" data-id="element-3277" data-mp="1k1yhlsk2krs2">
        <div className="max-w-4xl mx-auto" data-id="element-3278" data-mp="dmxumcd0vjxm">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#1a2b4a] mb-8 md:mb-12" data-id="element-3279" data-mp="dnpe4otchr7j">
            Wat andere baasjes zeggen
          </h2>
          <div ref={trustpilotGridRef} className="trustpilot-widget" data-locale="nl-NL" data-template-id="539ad60defb9600b94d7df2c" data-businessunit-id="65017f0060003ffc450ac45e" data-style-height="500px" data-style-width="100%" data-theme="light" data-stars="4,5" data-review-languages="nl" data-id="element-3280" data-mp="2kb6kpuhptbs4">
            <a href="https://nl.trustpilot.com/review/stresslessdogs.nl" target="_blank" rel="noopener noreferrer" data-id="element-3281" data-mp="332h841o8f6f9">
              Trustpilot
            </a>
          </div>
        </div>
      </section>

      {/* 9. FAQ SECTION */}
      <section className="py-12 md:py-20 px-4 bg-[#FAFAF7]" data-id="element-3282" data-mp="3h177jnyixi6s">
        <div className="max-w-3xl mx-auto" data-id="element-3283" data-mp="3gzcu0upl0scq">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#1a2b4a] mb-8 md:mb-12" data-id="element-3284" data-mp="1imcgvymeuw8j">
            Veelgestelde vragen
          </h2>
          <div className="space-y-3" data-id="element-3285" data-mp="1mnpoad55j7hk">
            {faqs.map((faq, i) => <div key={i} className="border border-gray-200 rounded-xl overflow-hidden bg-[#FAFAF7]" data-id="element-3286" data-mp="13pw9a030lsj8" data-mpi={i}>
                <button onClick={() => toggleFaq(i)} className="w-full flex justify-between items-center p-5 text-left hover:bg-gray-50 transition-colors" data-id="element-3287" data-mp="1cjhn9h7cv5rl">
                  <span className="font-bold text-[#1a2b4a] pr-4" data-id="element-3288" data-mp="ks1rawjws6op">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-3289" data-mp="fu0bckhs4nhg" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-3290" data-mp="tu2o5sb8cqpu" />}
                </button>
                {openFaq === i && <div className="p-5 pt-0 text-gray-600 bg-[#FAFAF7] leading-relaxed" data-id="element-3291" data-mp="2586g6sy9c4s9">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA SECTION */}
      <section className="py-16 md:py-24 px-4 bg-[#1a2b4a] text-white text-center" data-id="element-3292" data-mp="28osumcesq2fw">
        <div className="max-w-2xl mx-auto" data-id="element-3293" data-mp="2ct8zqbgl55u2">
          <p className="text-lg md:text-xl text-blue-200 mb-6 font-medium" data-id="element-3294" data-mp="1yrwmi7st7ibv">
            Na zondag 28 juni 23:59 is de prijs weer €87
          </p>

          <a href={CHECKOUT_URL} className="flex items-center justify-center w-full md:w-auto md:inline-flex md:mx-auto bg-[#e85d75] hover:bg-[#d64c64] text-white font-bold py-4 px-6 md:px-10 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl mb-10" data-id="element-3295" data-mp="23rsqw85f5bry">
            Ja, ik wil de cursus met 50% korting →
          </a>

          <div className="flex flex-col items-center" data-id="element-3296" data-mp="2mpbzxbrvmued">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-white/20 mb-4" data-id="element-3297" data-mp="2mo0z9ktqa2rh">
              <img src="https://cdn.magicpatterns.com/uploads/m4muL5gCGsQb9Rc66zAJPA/Amee_wandelen_foto2.png" alt="Amee de Bruijn" className="w-full h-full object-cover" data-id="element-3298" data-mp="3ae4wnagbp6a1" />
            </div>
            <p className="font-serif text-2xl text-blue-100" data-id="element-3299" data-mp="pz44o8ilqwt8">Liefs, Amee</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-10 px-4 border-t border-gray-200" data-id="element-3300" data-mp="ure209m1qz5j">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:justify-between items-center gap-6" data-id="element-3301" data-mp="zonp3ilyqyq7">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6YKzftRpGkz4jKTtn8FQxB/Logo_Stressless_Dogs_%288%29_%284%29.png" alt="Stressless Dogs" className="h-8 object-contain" data-id="element-3302" data-mp="238exg7isy2j" />
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500" data-id="element-3303" data-mp="c2h7c4mqm4ms">
            <a href="#" className="hover:text-gray-900" data-id="element-3304" data-mp="g311czc0ks4p">
              Privacybeleid
            </a>
            <a href="#" className="hover:text-gray-900" data-id="element-3305" data-mp="1nhcktn9ne01r">
              Algemene Voorwaarden
            </a>
            <a href="mailto:amee@stresslessdogs.nl" className="hover:text-gray-900" data-id="element-3306" data-mp="1riprfzkke79q">
              Contact
            </a>
          </div>
          <p className="text-sm text-gray-400" data-id="element-3307" data-mp="37y3y8j744xfx">
            © {new Date().getFullYear()} Stressless Dogs
          </p>
        </div>
      </footer>
    </div>;
}